import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

interface PerformanceBenchmarkingMatrixProps {
  data: {
    benchmarking_matrix: Array<{
      entity_name: string;
      entity_type: 'department' | 'team' | 'plant';
      efficiency_score: number;
      quality_score: number;
      speed_score: number;
      innovation_score: number;
      collaboration_score: number;
      overall_performance: number;
      workshop_count: number;
      success_rate: number;
    }>;
    performance_dimensions: {
      efficiency: {
        top_performers: Array<{ name: string; score: number; }>;
        average_score: number;
        benchmark: number;
      };
      quality: {
        top_performers: Array<{ name: string; score: number; }>;
        average_score: number;
        benchmark: number;
      };
      speed: {
        top_performers: Array<{ name: string; score: number; }>;
        average_score: number;
        benchmark: number;
      };
      innovation: {
        top_performers: Array<{ name: string; score: number; }>;
        average_score: number;
        benchmark: number;
      };
      collaboration: {
        top_performers: Array<{ name: string; score: number; }>;
        average_score: number;
        benchmark: number;
      };
    };
    best_practices: Array<{
      practice: string;
      top_performers: string[];
      impact_score: number;
      implementation_difficulty: 'low' | 'medium' | 'high';
      description: string;
    }>;
    improvement_opportunities: Array<{
      entity: string;
      dimension: string;
      current_score: number;
      potential_score: number;
      improvement_potential: number;
      recommendations: string[];
    }>;
  };
  loading?: boolean;
}

const PerformanceBenchmarkingMatrix: React.FC<PerformanceBenchmarkingMatrixProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Benchmarking Matrix</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Benchmarking Matrix</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No benchmarking data available
        </div>
      </div>
    );
  }

  const getPerformanceLevel = (score: number) => {
    if (score >= 90) return { level: 'Exceptional', color: '#10b981' };
    if (score >= 80) return { level: 'Excellent', color: '#3b82f6' };
    if (score >= 70) return { level: 'Good', color: '#8b5cf6' };
    if (score >= 60) return { level: 'Average', color: '#f59e0b' };
    return { level: 'Below Average', color: '#ef4444' };
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'low': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'high': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getEntityTypeColor = (type: string) => {
    switch (type) {
      case 'department': return '#3b82f6';
      case 'team': return '#10b981';
      case 'plant': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  // Prepare radar chart data
  const radarData = data.benchmarking_matrix.map(entity => ({
    subject: entity.entity_name,
    efficiency: entity.efficiency_score,
    quality: entity.quality_score,
    speed: entity.speed_score,
    innovation: entity.innovation_score,
    collaboration: entity.collaboration_score,
    fullMark: 100
  }));

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Benchmarking Matrix</h3>
      
      {/* Performance Scatter Plot */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Efficiency vs Quality Performance Matrix</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart data={data.benchmarking_matrix} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="efficiency_score" 
                name="Efficiency Score"
                tick={{ fontSize: 12 }}
                label={{ value: 'Efficiency Score', position: 'insideBottom', offset: -5 }}
              />
              <YAxis 
                dataKey="quality_score" 
                name="Quality Score"
                tick={{ fontSize: 12 }}
                label={{ value: 'Quality Score', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                formatter={(value: number, name: string) => {
                  if (name === 'efficiency_score') return [`${(value || 0).toFixed(1)}`, 'Efficiency Score'];
                  if (name === 'quality_score') return [`${(value || 0).toFixed(1)}`, 'Quality Score'];
                  if (name === 'overall_performance') return [`${(value || 0).toFixed(1)}`, 'Overall Performance'];
                  if (name === 'workshop_count') return [`${value || 0}`, 'Workshop Count'];
                  if (name === 'success_rate') return [`${(value || 0).toFixed(1)}%`, 'Success Rate'];
                  return [value || 0, name];
                }}
                labelFormatter={(label, payload) => {
                  if (payload && payload[0]) {
                    return `${payload[0].payload.entity_name} (${payload[0].payload.entity_type})`;
                  }
                  return '';
                }}
              />
              <Scatter 
                dataKey="quality_score" 
                fill="#3b82f6"
                r={6}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Dimensions Radar */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Performance Dimensions Comparison</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData.slice(0, 5)} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10 }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Radar
                name="Efficiency"
                dataKey="efficiency"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.1}
              />
              <Radar
                name="Quality"
                dataKey="quality"
                stroke="#10b981"
                fill="#10b981"
                fillOpacity={0.1}
              />
              <Radar
                name="Speed"
                dataKey="speed"
                stroke="#8b5cf6"
                fill="#8b5cf6"
                fillOpacity={0.1}
              />
              <Radar
                name="Innovation"
                dataKey="innovation"
                stroke="#f59e0b"
                fill="#f59e0b"
                fillOpacity={0.1}
              />
              <Radar
                name="Collaboration"
                dataKey="collaboration"
                stroke="#ef4444"
                fill="#ef4444"
                fillOpacity={0.1}
              />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Dimensions Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Top Performers by Dimension</h4>
          <div className="space-y-4">
            {Object.entries(data.performance_dimensions).map(([dimension, data]) => {
              const performance = getPerformanceLevel(data.average_score);
              return (
                <div key={dimension} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-gray-900 capitalize">{dimension}</span>
                    <span className="text-sm px-2 py-1 rounded" style={{ 
                      backgroundColor: performance.color + '20', 
                      color: performance.color 
                    }}>
                      {performance.level}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    Average: {(data.average_score || 0).toFixed(1)} | Benchmark: {(data.benchmark || 0).toFixed(1)}
                  </div>
                  <div className="space-y-1">
                    {data.top_performers.slice(0, 3).map((performer, index) => (
                      <div key={index} className="flex justify-between text-xs">
                        <span className="text-gray-700">#{index + 1} {performer.name}</span>
                        <span className="font-medium text-gray-900">{(performer.score || 0).toFixed(1)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-3">Best Practices</h4>
          <div className="space-y-3">
            {data.best_practices.map((practice, index) => (
              <div key={index} className="p-3 bg-green-50 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <span className="font-medium text-green-900">{practice.practice}</span>
                  <span className="text-xs px-2 py-1 rounded" style={{ 
                    backgroundColor: getDifficultyColor(practice.implementation_difficulty) + '20', 
                    color: getDifficultyColor(practice.implementation_difficulty) 
                  }}>
                    {practice.implementation_difficulty.toUpperCase()}
                  </span>
                </div>
                <div className="text-sm text-green-800 mb-2">{practice.description}</div>
                <div className="flex justify-between text-xs text-green-600">
                  <span>Impact: {(practice.impact_score || 0).toFixed(1)}%</span>
                  <span>Top Performers: {(practice.top_performers || []).length}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Improvement Opportunities */}
      <div>
        <h4 className="font-medium text-gray-900 mb-3">Improvement Opportunities</h4>
        <div className="space-y-3">
          {data.improvement_opportunities.map((opportunity, index) => {
            const improvement = getPerformanceLevel(opportunity.improvement_potential);
            return (
              <div key={index} className="p-4 bg-blue-50 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="font-medium text-blue-900">{opportunity.entity}</span>
                    <span className="text-sm text-blue-700 ml-2">({opportunity.dimension})</span>
                  </div>
                  <span className="text-sm font-bold text-blue-600">
                    +{(opportunity.improvement_potential || 0).toFixed(1)}%
                  </span>
                </div>
                <div className="text-sm text-blue-800 mb-2">
                  Current: {(opportunity.current_score || 0).toFixed(1)} → Potential: {(opportunity.potential_score || 0).toFixed(1)}
                </div>
                <div className="text-xs text-blue-600">
                  <strong>Recommendations:</strong> {(opportunity.recommendations || []).join(', ')}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Performance Level Legend</h4>
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
            <span className="text-gray-600">Exceptional (90-100%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-blue-500 rounded mr-2"></div>
            <span className="text-gray-600">Excellent (80-89%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-purple-500 rounded mr-2"></div>
            <span className="text-gray-600">Good (70-79%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-yellow-500 rounded mr-2"></div>
            <span className="text-gray-600">Average (60-69%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
            <span className="text-gray-600">Below Average (&lt;60%)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceBenchmarkingMatrix;

